import { Markdown } from '@cadstrom/sigma-engine-shared';
import { visit } from 'unist-util-visit';

import type { Parents, Root, Text } from 'mdast';
import type { Directives } from 'mdast-util-directive';
import type { ComponentType } from 'react';
import type { Plugin } from 'unified';
import type { Node } from 'unist';

export const AllDirectiveTypes = [
	Markdown.NodeTypes.TextDirective,
	Markdown.NodeTypes.LeafDirective,
	Markdown.NodeTypes.ContainerDirective,
] as const;

type DirectiveNodeType = (typeof AllDirectiveTypes)[number];

const SHORTHAND_BOUNDARY_CHARS = /[A-Za-z0-9_./:+-]/;

export type DirectivePlugin = Plugin<[], Root>;

export interface RemarkMarkdownDirectiveDefinition<
	Directive extends Markdown.DirectiveDefinition = Markdown.DirectiveDefinition,
> {
	allowedAttributes: Directive['allowedAttributes'];
	directive: DirectivePlugin;
	item: ComponentType<any>;
}

export interface BasicRemarkPluginOptions {
	/** Property name exposed to the rendered React component for the directive's textual content. */
	contentPropName: string;
	/**
	 * Optional global regex (capture group 1 must yield the reference value) for shorthand
	 * occurrences such as `@U1` or `#NetA`. When provided, matching plain-text spans are split
	 * into text directive nodes named after this plugin before directive processing runs.
	 */
	shorthandPattern?: RegExp;
}

/**
 * Builds a remark plugin that processes directives named `name` into hast nodes whose properties
 * carry the directive value (under `options.contentPropName`) and any directive attributes.
 *
 * When `options.shorthandPattern` is supplied, the plugin first walks plain text and expands any
 * matches into text directive nodes of the same `name`, so shorthand and explicit directives flow
 * through one definition.
 *
 * @param name - Directive name to handle (also used when expanding shorthand into directives).
 * @param directives - Directive node types this plugin should react to.
 * @param options - Behaviour options; see {@link BasicRemarkPluginOptions}.
 */
export function BasicRemarkPlugin(
	name: string,
	directives: Markdown.NodeTypes | Markdown.NodeTypes[],
	options: BasicRemarkPluginOptions,
): DirectivePlugin {
	const directiveFilter = getDirectiveFilter(directives);
	const { contentPropName, shorthandPattern } = options;

	return () => (tree) => {
		if (shorthandPattern !== undefined) {
			expandShorthandReferences(tree, name, shorthandPattern);
		}

		visit(tree, directiveFilter, (node) => {
			if (!isDirectiveNode(node) || node.name !== name) {
				return;
			}

			const directiveValue = getDirectiveValue(node);
			const data = node.data ?? (node.data = {});

			if (node.type === Markdown.NodeTypes.ContainerDirective) {
				removeContainerDirectiveLabel(node);
			}

			data.hName = node.name;
			data.hProperties = {
				...node.attributes,
				...(directiveValue === undefined ? {} : { [contentPropName]: directiveValue }),
			};
		});
	};
}

function expandShorthandReferences(tree: Root, directiveName: string, pattern: RegExp) {
	visit(tree, 'text', (node, index, parent) => {
		if (!isTextNode(node) || !isParentNode(parent) || index === undefined || isShorthandSkippedParent(parent)) {
			return;
		}

		const replacements = splitShorthandTextNode(node, directiveName, pattern);
		if (replacements === undefined) {
			return;
		}

		parent.children.splice(index, 1, ...replacements);
		return index + replacements.length;
	});
}

function splitShorthandTextNode(
	node: Text,
	directiveName: string,
	pattern: RegExp,
): Array<Text | Directives> | undefined {
	const { value } = node;
	pattern.lastIndex = 0;

	const matches: Array<{ index: number; raw: string; captured: string }> = [];
	for (const match of value.matchAll(pattern)) {
		const captured = match[1];
		if (captured === undefined || !isShorthandStartBoundary(value, match.index)) {
			continue;
		}
		matches.push({ index: match.index, raw: match[0], captured });
	}

	if (matches.length === 0) {
		return undefined;
	}

	const replacements: Array<Text | Directives> = [];
	let cursor = 0;

	for (const { index, raw, captured } of matches) {
		if (index > cursor) {
			replacements.push({ type: 'text', value: value.slice(cursor, index) });
		}
		replacements.push({
			type: Markdown.NodeTypes.TextDirective,
			name: directiveName,
			attributes: {},
			children: [{ type: 'text', value: captured }],
		});
		cursor = index + raw.length;
	}

	if (cursor < value.length) {
		replacements.push({ type: 'text', value: value.slice(cursor) });
	}

	return replacements;
}

function isShorthandStartBoundary(value: string, index: number): boolean {
	return index === 0 || !SHORTHAND_BOUNDARY_CHARS.test(value.charAt(index - 1));
}

function isShorthandSkippedParent(parent: Parents): boolean {
	return parent.type === 'link' || parent.type === 'linkReference';
}

function getDirectiveFilter(directives: Markdown.NodeTypes | Markdown.NodeTypes[]): DirectiveNodeType[] {
	const requested = Array.isArray(directives) ? directives : [directives];

	if (requested.includes(Markdown.NodeTypes.AllDirectives)) {
		return [...AllDirectiveTypes];
	}

	return requested.filter(
		(directive): directive is DirectiveNodeType => directive !== Markdown.NodeTypes.AllDirectives,
	);
}

export function isDirectiveNode(node: Node): node is Directives {
	return AllDirectiveTypes.some((directiveType) => (directiveType as string) === node.type);
}

function getDirectiveValue(node: Directives): string | undefined {
	const nodes =
		node.type === Markdown.NodeTypes.ContainerDirective ? getContainerDirectiveTitleNodes(node) : node.children;
	const value = collectText(nodes).trim();

	return value === '' ? undefined : value;
}

export function getContainerDirectiveTitleNodes(node: Directives): Node[] {
	if (node.type !== Markdown.NodeTypes.ContainerDirective) {
		return [];
	}

	const firstChild = node.children[0];
	if (firstChild?.type !== 'paragraph' || firstChild.data?.directiveLabel !== true) {
		return [];
	}

	return firstChild.children;
}

function removeContainerDirectiveLabel(node: Directives) {
	if (node.type !== Markdown.NodeTypes.ContainerDirective) {
		return;
	}

	const firstChild = node.children[0];
	if (firstChild?.type === 'paragraph' && firstChild.data?.directiveLabel === true) {
		node.children = node.children.slice(1);
	}
}

export function collectText(nodes: readonly Node[]): string {
	return nodes
		.map((node) => {
			if ('value' in node && typeof node.value === 'string') {
				return node.value;
			}

			if ('children' in node && Array.isArray(node.children)) {
				return collectText(node.children);
			}

			return '';
		})
		.join('');
}

function isTextNode(node: Node): node is Text {
	return node.type === 'text' && 'value' in node && typeof node.value === 'string';
}

export function isParentNode(node: Node | undefined): node is Parents {
	return node !== undefined && 'children' in node && Array.isArray(node.children);
}

/**
 * Builds a remark plugin that handles a container directive named `name`, collects the raw body
 * text as a JSON string, and passes it to the React component via a `jsonData` prop. Children are
 * suppressed so the raw JSON is not rendered as markdown content inside the component.
 *
 * The body is sliced from the original source by position rather than collected from the parsed
 * children: inline markdown constructs inside compact JSON (e.g. `:true` parsing as a text
 * directive) would otherwise be consumed and corrupt the payload.
 *
 * Usage in markdown:
 * ```
 * :::directive-name
 * [{"field":"value"}]
 * :::
 * ```
 *
 * @param name - Container directive name to match.
 */
export function JsonBodyRemarkPlugin(name: string): DirectivePlugin {
	return () => (tree, file) => {
		visit(tree, [Markdown.NodeTypes.ContainerDirective], (node) => {
			if (!isDirectiveNode(node) || node.name !== name) {
				return;
			}

			// Remove directive label paragraph so only the body remains.
			const firstChild = node.children[0];
			if (firstChild?.type === 'paragraph' && firstChild.data?.directiveLabel === true) {
				node.children = node.children.slice(1);
			}

			const jsonData = rawSourceText(node.children, file.value) ?? collectText(node.children).trim();
			const data = node.data ?? (node.data = {});
			data.hName = name;
			data.hProperties = { jsonData };
			// Suppress children — data is consumed as a prop, not rendered as content.
			data.hChildren = [];
		});
	};
}

/**
 * Slices the original markdown source spanned by a run of sibling nodes.
 *
 * @param nodes - The nodes whose combined source range to extract.
 * @param source - The vfile's source value.
 * @returns The trimmed raw text, or undefined when positions or source are unavailable.
 */
function rawSourceText(nodes: readonly Node[], source: unknown): string | undefined {
	if (typeof source !== 'string' || nodes.length === 0) {
		return undefined;
	}
	const start = nodes[0]?.position?.start.offset;
	const end = nodes[nodes.length - 1]?.position?.end.offset;
	if (start === undefined || end === undefined) {
		return undefined;
	}
	return source.slice(start, end).trim();
}
